import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Mic, Square, Loader2 } from "lucide-react";

export default function MeetingRecorder({ modelKey, language, onDone }) {
  const { t } = useLang();
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
  }, []);

  const fmt = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const r = (s % 60).toString().padStart(2, "0");
    return `${m}:${r}`;
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
        setProcessing(true);
        try {
          const form = new FormData();
          form.append("file", blob, "meeting.webm");
          const { data } = await api.post(
            `/meeting/transcribe?language=${encodeURIComponent(language || "en")}&model=${encodeURIComponent(modelKey || "claude")}`,
            form,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          onDone?.(data);
        } catch (e) {
          onDone?.(null, e?.response?.data?.detail || e?.message || "Transcription failed");
        } finally {
          setProcessing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      onDone?.(null, "Microphone permission denied.");
    }
  };

  const stop = () => {
    if (!mediaRecorderRef.current) return;
    try { mediaRecorderRef.current.stop(); } catch {}
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  return (
    <div className="flex flex-col items-center justify-center py-10 gap-5" data-testid="meeting-recorder">
      <div className={`relative w-24 h-24 rounded-full grid place-items-center border ${recording ? "border-primary" : "border-border"}`}>
        {recording && <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />}
        <div className={`w-16 h-16 rounded-full grid place-items-center ${recording ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
          {processing ? <Loader2 size={22} className="animate-spin" /> : <Mic size={22} />}
        </div>
      </div>
      <div className="text-center">
        <p className="text-lg font-display" data-testid="meeting-status">
          {processing ? t("meeting.processing") : recording ? t("meeting.recording") : "Ready to record"}
        </p>
        <p className="text-sm text-muted-foreground mt-1" data-testid="meeting-elapsed">{fmt(elapsed)}</p>
      </div>
      {!recording ? (
        <button
          data-testid="meeting-start-btn"
          onClick={start}
          disabled={processing}
          className="rounded-full h-12 px-6 bg-primary text-primary-foreground hover:brightness-110 transition disabled:opacity-60"
        >
          {t("meeting.record")}
        </button>
      ) : (
        <button
          data-testid="meeting-stop-btn"
          onClick={stop}
          className="rounded-full h-12 px-6 bg-destructive text-destructive-foreground hover:brightness-110 transition inline-flex items-center gap-2"
        >
          <Square size={14} /> {t("meeting.stop")}
        </button>
      )}
      <p className="text-xs text-muted-foreground text-center max-w-sm">{t("meeting.hint")}</p>
    </div>
  );
}
